import type { AccountVerificationResponse } from "@/modules/verification/types/verification-api.types";
import type { OrganizationStatus } from "@/shared/types/organization.entity";

export interface AdminOrganizationMatchedUser {
  id: string;
  full_name: string;
  email: string;
}

/** The slice of `GET /internal/organizations` this directory renders. */
export interface AdminOrganization {
  id: string;
  name: string;
  email: string;
  country: string | null;
  status: OrganizationStatus;
  payout_batches_enabled: boolean;
  /** Whoever prepares a batch may approve it, even when a colleague could. */
  payout_batches_self_approval_enabled: boolean;
  breb_receiving_key_enabled: boolean;
  otc_enabled: boolean;
  card_deposits_enabled: boolean;
  card_cop_top_ups_enabled: boolean;
  breb_qr_payments_enabled: boolean;
  /**
   * Paying Bre-B QRs Coloca cannot pay yet (a fixed convenience fee): they complete but never reach the
   * merchant.
   */
  breb_qr_unsupported_payments_enabled: boolean;
  /** Opt-in card wallet: loading crypto cards from it and withdrawing card balance back to it. */
  card_wallet_enabled: boolean;
  /** Its own card design (colors and logo, co-branded) in the secure card view; off, that view shows Coloca's. */
  can_customize_card_design: boolean;
  created_at: string;
  /** Only on a searched page: the members whose name or email matched. */
  matched_users?: AdminOrganizationMatchedUser[];
}

export type CardOnboardingStage =
  | "not_registered"
  | "in_progress"
  | "paused"
  | "in_review"
  | "enabled"
  | "rejected";

export type CardOnboardingPauseReason =
  | "business_identity"
  | "missing_identity_fields"
  | "missing_email"
  | "missing_address"
  | "unmappable_state"
  | "missing_client_data"
  | "documents_unavailable"
  | "already_registered"
  | "other";

export type OrganizationMemberStatus = "active" | "inactive" | "suspended" | "pending_verification";

export interface OrganizationOverviewUser {
  id: string;
  full_name: string;
  email: string;
  status: OrganizationMemberStatus;
  last_login_at: string | null;
  two_factor_enabled: boolean;
}

/** `GET /internal/organizations/:id/overview`, staff only. Money is a decimal string. */
export interface OrganizationOverview {
  organization: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    country: string | null;
    status: OrganizationStatus;
    /** Code identifier only; rendered through the Coloca-branded label. */
    card_provider: "BLOQUE" | "CRYPTO_MATE" | "SUMSUB" | null;
    created_at: string;
    created_by: { full_name: string; email: string } | null;
  };
  verification: AccountVerificationResponse;
  identity: {
    id: string;
    type: "individual" | "business";
    /** Null for a business: its name is the organization's. */
    name: string | null;
    identification_type: string | null;
    identification_number: string | null;
    country: string | null;
    kyc_level: string | null;
  } | null;
  card_onboarding: {
    provider: "BLOQUE" | "CRYPTO_MATE" | "SUMSUB" | null;
    organization_identity: {
      stage: CardOnboardingStage;
      pause_reason: CardOnboardingPauseReason | null;
      updated_at: string;
    } | null;
    cardholders: Record<CardOnboardingStage, number> & { total: number };
  };
  users: {
    counts: Record<OrganizationMemberStatus, number> & { total: number };
    items: OrganizationOverviewUser[];
  };
  wallets: Array<{ currency: string; status: string; balance_available: string; balance_pending: string }>;
  cards: { total: number; active: number; frozen: number };
  clients: { total: number };
  breb_keys: { active: number; primary_key: string | null };
  api_keys: { active: number; last_used_at: string | null };
  activity: {
    last_payin_at: string | null;
    payins_last_30_days: number;
    last_payout_at: string | null;
    payouts_last_30_days: number;
  };
}

export interface AdminOrganizationsPage {
  data: AdminOrganization[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

export type PayoutBatchesFilter = "all" | "enabled" | "disabled";

export interface AdminOrganizationsQuery {
  skip: number;
  limit: number;
  search?: string;
  status?: OrganizationStatus;
  payoutBatchesEnabled?: boolean;
}

export interface UpdateOrganizationFeaturesPayload {
  payout_batches_enabled?: boolean;
  payout_batches_self_approval_enabled?: boolean;
  breb_receiving_key_enabled?: boolean;
  otc_enabled?: boolean;
  card_deposits_enabled?: boolean;
  card_cop_top_ups_enabled?: boolean;
  breb_qr_payments_enabled?: boolean;
  breb_qr_unsupported_payments_enabled?: boolean;
  card_wallet_enabled?: boolean;
  can_customize_card_design?: boolean;
}

export type OrganizationFeature = keyof UpdateOrganizationFeaturesPayload;

/**
 * Where a general switch stands (coloca-nest `GlobalFeatureMode`). `off_for_all` wins over everything, the
 * organizations switched on included; their own switches are kept and count again once it is lifted.
 */
export type GlobalFeatureMode = "per_organization" | "on_for_all" | "off_for_all";

export const GLOBAL_FEATURE_MODES: readonly GlobalFeatureMode[] = [
  "per_organization",
  "on_for_all",
  "off_for_all",
];

/** Switches that apply to every organization at once. */
export interface GlobalFeatures {
  /** Batch payouts for the members of every organization (staff always reach them). */
  payout_batches_enabled: GlobalFeatureMode;
  /** Creating the organization's own Bre-B key. */
  breb_receiving_key_enabled: GlobalFeatureMode;
  /** Buying and selling USDT. */
  otc_enabled: GlobalFeatureMode;
  /** The deposit address (QR) of every crypto card. */
  card_deposits_enabled: GlobalFeatureMode;
  /** Loading crypto cards from the organization's COP wallet. */
  card_cop_top_ups_enabled: GlobalFeatureMode;
  /** Paying Bre-B QRs; on for all by default. */
  breb_qr_payments_enabled: GlobalFeatureMode;
}

export type GlobalFeature = keyof GlobalFeatures;

/**
 * The wire: each boolean is `true` only while on for all; `modes` says the rest (absent before coloca-nest
 * had it).
 */
export type GlobalFeaturesResponse = Partial<Record<GlobalFeature, boolean>> & {
  modes?: Partial<Record<GlobalFeature, string>>;
};

/** `modes` sets a switch outright; the older boolean only moves between on for all and per organization. */
export type UpdateGlobalFeaturesPayload = Partial<Record<GlobalFeature, boolean>> & {
  modes?: Partial<GlobalFeatures>;
};
